import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { RecurringInvoiceSchedulesTable } from "@/components/RecurringInvoiceSchedulesTable";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function RecurringInvoiceSchedulesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [organisationIdInput, setOrganisationIdInput] = useState(
    searchParams.get("organisationId") ?? "",
  );
  const [customerIdInput, setCustomerIdInput] = useState(searchParams.get("customerId") ?? "");
  const debouncedOrganisationId = useDebouncedValue(organisationIdInput.trim());
  const debouncedCustomerId = useDebouncedValue(customerIdInput.trim());

  useEffect(() => {
    setSearchParams(
      (params) => {
        if (debouncedOrganisationId) {
          params.set("organisationId", debouncedOrganisationId);
        } else {
          params.delete("organisationId");
        }
        if (debouncedCustomerId) {
          params.set("customerId", debouncedCustomerId);
        } else {
          params.delete("customerId");
        }
        return params;
      },
      { replace: true },
    );
  }, [debouncedOrganisationId, debouncedCustomerId, setSearchParams]);

  // The API expects uuid-format values — only apply a filter once it's a full UUID.
  const organisationId = UUID_PATTERN.test(debouncedOrganisationId)
    ? debouncedOrganisationId
    : undefined;
  const customerId = UUID_PATTERN.test(debouncedCustomerId) ? debouncedCustomerId : undefined;

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-6 text-2xl font-bold">Recurring Invoice Schedules</h1>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by organisation ID…"
          value={organisationIdInput}
          onChange={(e) => setOrganisationIdInput(e.target.value)}
          aria-invalid={!!debouncedOrganisationId && !organisationId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
        <Input
          placeholder="Filter by customer ID…"
          value={customerIdInput}
          onChange={(e) => setCustomerIdInput(e.target.value)}
          aria-invalid={!!debouncedCustomerId && !customerId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
      </div>

      <RecurringInvoiceSchedulesTable
        organisationId={organisationId}
        customerId={customerId}
        showAllColumns
      />
    </div>
  );
}
